import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  StatCard,
  SectionCard,
  PrimaryButton,
  StatusBadge,
  GhostButton,
} from "@/components/ui-bits";
import { fmtBRL } from "@/lib/format";
import {
  Plus,
  Calendar,
  MapPin,
  Users,
  ChevronRight,
  X,
  LayoutGrid,
  List,
  Kanban,
  Loader2,
  Trash2,
  CheckCircle2,
  Clock,
  TrendingUp,
  AlertTriangle,
  ArrowUpDown,
  Link2,
  Copy,
} from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { eventBudgetService, type Event as RealEvent } from "@/services/event-budget-service";
import { PageHeader } from "@/components/AppShell";
import { migrateLegacyStoreToSupabase } from "@/lib/migration";
import { EventKanban } from "@/components/kanban/EventKanban";
import { type KanbanColumnId } from "@/lib/kanban-pipeline";
import { budgetRequestService } from "@/services/budget-request-service";
import {
  getPendingPublicBudgetRequests,
  isPendingPublicBudgetRequest,
} from "@/lib/public-budget-requests";
import { deleteEventFromList } from "@/lib/event-list-deletion";

const VIEW_STORAGE_KEY = "goatbar:eventos:view";

function getStoredViewMode(): "lista" | "kanban" | "calendario" {
  if (typeof window === "undefined") return "lista";
  try {
    const saved = localStorage.getItem(VIEW_STORAGE_KEY);
    if (saved === "lista" || saved === "kanban" || saved === "calendario") {
      return saved;
    }
  } catch (_e) {
    // Ignora erro de acesso a localStorage em ambientes restritos
  }
  return "lista";
}

export const Route = createFileRoute("/eventos/")({
  component: EventosIndex,
});

function EventosIndex() {
  const navigate = useNavigate();
  const [eventos, setEventos] = useState<RealEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [publicLink, setPublicLink] = useState("");
  const [creatingLink, setCreatingLink] = useState(false);
  const [viewMode, setViewModeState] = useState<"lista" | "kanban" | "calendario">(
    getStoredViewMode,
  );
  const [pendingOverrides, setPendingOverrides] = useState<Record<string, KanbanColumnId>>({});
  const [savingEventIds, setSavingEventIds] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<string>("pipeline");
  const [sortOrder, setSortOrder] = useState<"data" | "status">("data");

  const setViewMode = (mode: "lista" | "kanban" | "calendario") => {
    setViewModeState(mode);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, mode);
    } catch (_e) {
      // Ignora erro de acesso a localStorage em ambientes restritos
    }
  };
  const [form, setForm] = useState({
    nome: "",
    evento_nome: "",
    telefone: "",
    email: "",
    tipo: "Casamento",
    data: "",
    local: "",
    cidade: "São Paulo",
    convidados: 100,
    observacoes: "",
    lead_source: "",
    referral_name: "",
  });

  useEffect(() => {
    migrateLegacyStoreToSupabase()
      .catch((error) =>
        console.error("Falha na migração automática de dados legados (eventos).", error),
      )
      .finally(loadEvents);
  }, []);

  useEffect(() => {
    const channel = eventBudgetService.subscribeToEventChanges(loadEvents);
    return () => void channel.unsubscribe();
  }, []);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const data = await eventBudgetService.listEvents();
      setEventos(data);
    } catch (e) {
      console.error("Erro ao carregar eventos:", e);
    } finally {
      setLoading(false);
    }
  };

  const novosOrcamentosPublicos = getPendingPublicBudgetRequests(eventos);
  const eventosSemSolicitacoesPublicasPendentes = eventos.filter(
    (e) => !isPendingPublicBudgetRequest(e),
  );

  const eventosAtivos = eventosSemSolicitacoesPublicasPendentes.filter((e) => {
    const s = e.status?.toUpperCase();
    return !["CANCELADO", "PROPOSTA_RECUSADA"].includes(s);
  });

  const STATUS_ORDER: Record<string, number> = {
    NOVO_ORCAMENTO: 0,
    NOVO: 0,
    ORCAMENTO_ENVIADO: 1,
    AGUARDANDO_RETORNO: 2,
    AGUARDANDO_RESPOSTA: 2,
    DADOS_SOLICITADOS: 3,
    EM_ASSINATURA: 4,
    CONFIRMADO: 5,
    FINALIZADO: 6,
    REALIZADO: 6,
    CANCELADO: 7,
    PROPOSTA_RECUSADA: 7,
  };

  const filteredEventos = (() => {
    const filtered = eventosSemSolicitacoesPublicasPendentes.filter((e) => {
      const s = e.status?.toUpperCase() || "";
      if (statusFilter === "pipeline") {
        return !["FINALIZADO", "REALIZADO", "CANCELADO", "PROPOSTA_RECUSADA"].includes(s);
      }
      if (statusFilter === "ativos") {
        return !["CANCELADO", "PROPOSTA_RECUSADA"].includes(s);
      }
      if (statusFilter === "negociacao") {
        return [
          "NOVO_ORCAMENTO",
          "NOVO",
          "ORCAMENTO_ENVIADO",
          "AGUARDANDO_RETORNO",
          "AGUARDANDO_RESPOSTA",
          "DADOS_SOLICITADOS",
          "EM_ASSINATURA",
        ].includes(s);
      }
      if (statusFilter === "confirmados") return s === "CONFIRMADO";
      if (statusFilter === "finalizados") return ["FINALIZADO", "REALIZADO"].includes(s);
      if (statusFilter === "cancelados") return ["CANCELADO", "PROPOSTA_RECUSADA"].includes(s);
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (sortOrder === "data") {
        return (a.date || "").localeCompare(b.date || "");
      }
      const sa = STATUS_ORDER[a.status?.toUpperCase() || ""] ?? 99;
      const sb = STATUS_ORDER[b.status?.toUpperCase() || ""] ?? 99;
      if (sa !== sb) return sa - sb;
      return (a.date || "").localeCompare(b.date || "");
    });
  })();

  const eventosConfirmados = eventosAtivos.filter((e) => e.status?.toUpperCase() === "CONFIRMADO");
  const eventosFinalizados = eventosSemSolicitacoesPublicasPendentes.filter((e) =>
    ["FINALIZADO", "REALIZADO"].includes(e.status?.toUpperCase() || ""),
  );

  const { totalPago, totalAReceber } = (() => {
    let pago = 0;
    let aReceber = 0;
    [...eventosConfirmados, ...eventosFinalizados].forEach((e) => {
      // @ts-expect-error Erro legado pré-existente fora do escopo (Tipagem de BD desatualizada)
      const total = Number(e.current_budget_value || e.budget_value || 0);
      // @ts-expect-error Erro legado pré-existente fora do escopo (Tipagem de BD desatualizada)
      const percentPago = Number(e.payment_percent_received ?? e.paid_percentage ?? 0);
      const valorPago = total * (percentPago / 100);
      pago += valorPago;
      aReceber += Math.max(0, total - valorPago);
    });
    return { totalPago: pago, totalAReceber: aReceber };
  })();

  const receitaEnviados = eventosAtivos
    .filter((e) =>
      ["ORCAMENTO_ENVIADO", "AGUARDANDO_RESPOSTA", "DADOS_SOLICITADOS"].includes(
        e.status?.toUpperCase() || "",
      ),
    )
    .reduce((a, e) => a + (e.current_budget_value || 0), 0);

  const handleStatusChange = async (eventId: string, newStatus: KanbanColumnId) => {
    if (savingEventIds.has(eventId)) return;
    setPendingOverrides((prev) => ({ ...prev, [eventId]: newStatus }));
    setSavingEventIds((prev) => new Set(prev).add(eventId));

    try {
      await eventBudgetService.updateNegotiationStatus(eventId, newStatus);
      const data = await eventBudgetService.listEvents();
      setEventos(data);
    } catch (err: unknown) {
      console.error("Erro ao atualizar status do evento:", err);
      const errMsg = err instanceof Error ? err.message : "Falha na comunicação com o Supabase";
      toast.error(`Erro ao atualizar status: ${errMsg}`);
    } finally {
      setPendingOverrides((prev) => {
        const next = { ...prev };
        delete next[eventId];
        return next;
      });
      setSavingEventIds((prev) => {
        const next = new Set(prev);
        next.delete(eventId);
        return next;
      });
    }
  };

  const mesmoDiaEventos = form.data
    ? eventos.filter(
        (e) => e.date === form.data && !["cancelado", "proposta_recusada"].includes(e.status),
      )
    : [];

  const handleCreate = async () => {
    if (!form.nome || !form.data) return;
    try {
      const newEvent = await eventBudgetService.createEvent({
        client_name: form.nome,
        event_name: form.evento_nome,
        phone: form.telefone,
        email: form.email,
        event_type: form.tipo,
        date: form.data,
        event_location: form.local,
        city: form.cidade,
        guests: form.convidados,
        notes: form.observacoes,
        lead_source: form.lead_source,
        referral_name: form.referral_name,
        status: "novo_orcamento",
      });

      setShowModal(false);
      navigate({ to: "/eventos/$eventoId", params: { eventoId: newEvent.id } });
    } catch (e: any) {
      alert(`Erro ao criar evento: ${e.message || "Erro desconhecido"}`);
    }
  };

  const handleCreateLink = async () => {
    setCreatingLink(true);
    try {
      const result = await budgetRequestService.createBudgetRequestLink();
      setPublicLink(result.url);
      toast.success("Link de orçamento criado.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível gerar o link.");
    } finally {
      setCreatingLink(false);
    }
  };

  const renderEventRow = (e: RealEvent, compactPublic = false) => (
    <Link
      key={e.id}
      to="/eventos/$eventoId"
      params={{ eventoId: e.id }}
      className="flex items-start sm:items-center gap-3 sm:gap-4 p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-surface transition-all group"
    >
      <div className="h-10 w-10 rounded-lg bg-primary/15 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
        <Calendar className="h-5 w-5 text-primary" />
      </div>
      <div className="flex-1 min-w-0 pr-2 sm:pr-0">
        <div className="font-bold text-sm truncate group-hover:text-primary transition-colors">
          {e.event_name || e.client_name}
        </div>
        {compactPublic && (
          <span className="mt-1 inline-flex rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
            Solicitado pelo site
          </span>
        )}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[10px] text-muted-foreground uppercase tracking-wider font-bold">
          <span className="flex items-center gap-1">
            <Users className="h-3 w-3" /> {e.guests} pax
          </span>
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {e.event_location || "A definir"}
          </span>
          <span className="basis-full sm:basis-auto">
            {e.date
              ? new Date(e.date).toLocaleDateString("pt-BR", { timeZone: "UTC" })
              : "Data a definir"}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 sm:gap-4 shrink-0 self-center">
        {!compactPublic && (
          <>
            <div className="hidden sm:block text-right">
              <div className="text-sm font-bold text-foreground">
                {e.current_budget_value ? fmtBRL(e.current_budget_value) : "--"}
              </div>
              <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-tighter">
                {e.current_budget_value
                  ? `${fmtBRL(e.current_budget_value / e.guests)}/pessoa`
                  : "Orçamento em aberto"}
              </div>
            </div>
            <div
              className="hidden sm:block relative group/status"
              onClick={(ev) => ev.preventDefault()}
            >
              <StatusBadge status={(pendingOverrides[e.id] ?? e.status) as any} />
              <select
                className="absolute inset-0 opacity-0 cursor-pointer"
                value={pendingOverrides[e.id] ?? e.status}
                disabled={savingEventIds.has(e.id)}
                onChange={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  handleStatusChange(e.id, ev.target.value as KanbanColumnId);
                }}
              >
                <option value="novo_orcamento">Novo orçamento</option>
                <option value="orcamento_enviado">Orçamento enviado</option>
                <option value="aguardando_retorno">Aguardando retorno</option>
                <option value="dados_solicitados">Dados solicitados</option>
                <option value="em_assinatura">Em assinatura</option>
                <option value="confirmado">Confirmado</option>
                <option value="finalizado">Finalizado</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
          </>
        )}
        <button
          className="hidden sm:flex h-8 w-8 rounded-full items-center justify-center bg-background border border-border text-destructive hover:bg-destructive/10 transition-all"
          onClick={async (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            if (confirm("Excluir este orçamento definitivamente?")) {
              await deleteEventFromList(e.id, {
                deleteRequest: eventBudgetService.deletePublicBudgetRequest,
                removeFromList: (eventId) =>
                  setEventos((current) => current.filter((event) => event.id !== eventId)),
                reload: loadEvents,
                showSuccess: toast.success,
                showError: toast.error,
              });
            }
          }}
        >
          <Trash2 className="h-4 w-4" />
        </button>
        <div className="h-8 w-8 rounded-full flex items-center justify-center bg-background border border-border group-hover:border-primary/30 group-hover:text-primary transition-all">
          <ChevronRight className="h-4 w-4" />
        </div>
      </div>
    </Link>
  );

  return (
    <>
      <PageHeader
        title="Eventos e Orçamentos"
        subtitle="Pipeline de operações e orçamentos."
        action={
          <div className="flex flex-wrap gap-2">
            <GhostButton onClick={handleCreateLink} disabled={creatingLink}>
              {creatingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Gerar link de orçamento
            </GhostButton>
            <PrimaryButton onClick={() => setShowModal(true)}>
              <Plus className="h-4 w-4" /> Novo orçamento
            </PrimaryButton>
          </div>
        }
      />

      <div className="page-container space-y-7">
        {publicLink && (
          <div className="flex flex-col gap-3 rounded-xl border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold uppercase tracking-wide text-primary">Link para o cliente</div>
              <div className="mt-1 truncate text-sm">{publicLink}</div>
            </div>
            <GhostButton
              onClick={async () => {
                await navigator.clipboard.writeText(publicLink);
                toast.success("Link copiado.");
              }}
            >
              <Copy className="h-4 w-4" /> Copiar link
            </GhostButton>
          </div>
        )}

        {novosOrcamentosPublicos.length > 0 && (
          <SectionCard
            title={`Novos orçamentos solicitados (${novosOrcamentosPublicos.length})`}
            subtitle="Solicitações recebidas pelos links públicos. Abra o orçamento e avance o status para movê-lo ao pipeline."
          >
            <div className="space-y-2">
              {novosOrcamentosPublicos.map((e) => renderEventRow(e, true))}
            </div>
          </SectionCard>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4">
          <StatCard label="Total em pipeline" value={String(eventosAtivos.length)} icon={<TrendingUp className="h-4 w-4" />} />
          <StatCard label="Confirmados" value={String(eventosConfirmados.length)} icon={<CheckCircle2 className="h-4 w-4 text-success" />} />
          <StatCard label="Finalizados" value={String(eventosFinalizados.length)} icon={<Calendar className="h-4 w-4" />} />
          <StatCard label="Em negociação (R$)" value={fmtBRL(receitaEnviados)} icon={<Clock className="h-4 w-4 text-amber-400" />} />
          <StatCard label="Valor já pago" value={fmtBRL(totalPago)} icon={<CheckCircle2 className="h-4 w-4 text-success" />} highlight />
          <StatCard label="Valor a receber" value={fmtBRL(totalAReceber)} icon={<Clock className="h-4 w-4 text-amber-400" />} />
        </div>

        <SectionCard
          title="Pipeline de negociações"
          subtitle={loading ? "Carregando..." : `${filteredEventos.length} de ${eventosSemSolicitacoesPublicasPendentes.length} registros`}
          action={
            <div className="flex flex-wrap items-center gap-3">
              {viewMode !== "calendario" && (
                <>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-9 px-3 rounded-lg bg-input border border-border text-xs font-medium focus:ring-1 focus:ring-primary outline-none cursor-pointer text-foreground"
                    aria-label="Filtrar eventos por status"
                  >
                    <option value="pipeline">Pipeline (ativos)</option>
                    <option value="todos">Todos os registros</option>
                    <option value="negociacao">Em Negociação</option>
                    <option value="confirmados">Confirmados</option>
                    <option value="finalizados">Finalizados</option>
                    <option value="cancelados">Cancelados</option>
                  </select>

                  <div className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-input border border-border text-xs font-medium text-foreground">
                    <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <select
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value as "data" | "status")}
                      className="bg-transparent outline-none cursor-pointer"
                      aria-label="Ordenar eventos"
                    >
                      <option value="data">Por data do evento</option>
                      <option value="status">Por status do orçamento</option>
                    </select>
                  </div>
                </>
              )}
              <div className="flex bg-background border border-border rounded-lg p-0.5">
                <button onClick={() => setViewMode("lista")} aria-pressed={viewMode === "lista"} className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${viewMode === "lista" ? "bg-surface shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  <List className="h-3.5 w-3.5" /> Lista
                </button>
                <button onClick={() => setViewMode("kanban")} aria-pressed={viewMode === "kanban"} className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${viewMode === "kanban" ? "bg-surface shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  <Kanban className="h-3.5 w-3.5" /> Kanban
                </button>
                <button onClick={() => setViewMode("calendario")} aria-pressed={viewMode === "calendario"} className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${viewMode === "calendario" ? "bg-surface shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                  <LayoutGrid className="h-3.5 w-3.5" /> Calendário
                </button>
              </div>
            </div>
          }
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="h-8 w-8 text-primary animate-spin mb-4" />
              <p className="text-sm text-muted-foreground">Buscando eventos no Supabase...</p>
            </div>
          ) : viewMode === "lista" ? (
            <div className="space-y-2">
              {filteredEventos.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-8">Nenhum evento cadastrado para o filtro selecionado.</p>
              )}
              {filteredEventos.map((e) => renderEventRow(e))}
            </div>
          ) : viewMode === "kanban" ? (
            <EventKanban
              events={filteredEventos}
              statusFilter={statusFilter}
              savingEventIds={savingEventIds}
              pendingOverrides={pendingOverrides}
              onStatusChange={handleStatusChange}
            />
          ) : (
            <CalendarView eventosAtivos={eventosAtivos} />
          )}
        </SectionCard>
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg bg-surface border border-border rounded-2xl shadow-2xl">
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-border">
              <h2 className="font-display text-lg font-semibold">Novo orçamento</h2>
              <button onClick={() => setShowModal(false)} className="h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-background/40 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {mesmoDiaEventos.length > 0 && (
                <div className="p-4 rounded-lg bg-warning/10 border border-warning/20 text-warning flex gap-3 text-sm">
                  <AlertTriangle className="h-5 w-5 shrink-0" />
                  <div>
                    <div className="font-semibold">Atenção: já existe outro evento orçado ou confirmado para esta mesma data.</div>
                    <ul className="mt-2 list-disc list-inside space-y-1 opacity-80">
                      {mesmoDiaEventos.map((ev) => (
                        <li key={ev.id}>{ev.event_name || ev.client_name} ({ev.event_location}) - {ev.status.replace("_", " ")}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {[
                { label: "Nome do solicitante", key: "nome", type: "text", placeholder: "Ex: João Silva" },
                { label: "Nome do Evento / Casal (opcional)", key: "evento_nome", type: "text", placeholder: "Ex: Casamento João & Maria" },
                { label: "Contato (Telefone/WhatsApp)", key: "telefone", type: "tel", placeholder: "(11) 99999-9999" },
                { label: "E-mail (opcional)", key: "email", type: "email", placeholder: "cliente@email.com" },
                { label: "Data do evento", key: "data", type: "date", placeholder: "" },
                { label: "Local do evento", key: "local", type: "text", placeholder: "Nome do espaço (A definir)" },
                { label: "Cidade", key: "cidade", type: "text", placeholder: "São Paulo" },
              ].map(({ label, key, type, placeholder }) => (
                <div key={key}>
                  <label className="label-eyebrow block mb-2">{label}</label>
                  <input
                    type={type}
                    placeholder={placeholder}
                    value={(form as any)[key]}
                    onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                    className="w-full h-10 px-4 rounded-lg bg-input border border-border focus:border-primary focus:outline-none text-sm transition-colors"
                  />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-eyebrow block mb-2">Qtd Convidados</label>
                  <input
                    type="number"
                    value={form.convidados || ""}
                    onChange={(e) => setForm((p) => ({ ...p, convidados: e.target.value === "" ? 0 : Number(e.target.value) }))}
                    className="w-full h-10 px-4 rounded-lg bg-input border border-border focus:border-primary focus:outline-none text-sm transition-colors"
                  />
                </div>
                <div>
                  <label className="label-eyebrow block mb-2">Tipo</label>
                  <select value={form.tipo} onChange={(e) => setForm((p) => ({ ...p, tipo: e.target.value }))} className="w-full h-10 px-4 rounded-lg bg-input border border-border focus:border-primary focus:outline-none text-sm transition-colors">
                    {["Casamento", "Corporativo", "Aniversário", "Comemoração"].map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-eyebrow block mb-2">Canal de Origem</label>
                  <select value={form.lead_source || ""} onChange={(e) => setForm((p) => ({ ...p, lead_source: e.target.value }))} className="w-full h-10 px-4 rounded-lg bg-input border border-border focus:border-primary focus:outline-none text-sm transition-colors">
                    <option value="">A definir</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Google">Google</option>
                    <option value="WhatsApp">WhatsApp</option>
                    <option value="Indicação">Indicação</option>
                    <option value="Site">Site</option>
                  </select>
                </div>
                {form.lead_source === "Indicação" && (
                  <div>
                    <label className="label-eyebrow block mb-2">Quem indicou?</label>
                    <input type="text" placeholder="Nome da referência" value={form.referral_name || ""} onChange={(e) => setForm((p) => ({ ...p, referral_name: e.target.value }))} className="w-full h-10 px-4 rounded-lg bg-input border border-border focus:border-primary focus:outline-none text-sm transition-colors" />
                  </div>
                )}
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Observações gerais</label>
                <textarea rows={3} value={form.observacoes} onChange={(e) => setForm((p) => ({ ...p, observacoes: e.target.value }))} placeholder="Detalhes iniciais do cliente..." className="w-full px-4 py-3 rounded-lg bg-input border border-border focus:border-primary focus:outline-none text-sm transition-colors resize-none" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
              <GhostButton onClick={() => setShowModal(false)}>Cancelar</GhostButton>
              <PrimaryButton onClick={handleCreate} disabled={!form.nome || !form.data}>Avançar para orçamento</PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CalendarView({ eventosAtivos }: { eventosAtivos: RealEvent[] }) {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const days = Array.from({ length: 42 }, (_, i) => {
    const dayNum = i - firstDay + 1;
    if (dayNum > 0 && dayNum <= daysInMonth) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
      const dayEvents = eventosAtivos.filter(
        (e) => e.date === dateStr && e.status?.toUpperCase() === "CONFIRMADO",
      );
      return { dayNum, dateStr, events: dayEvents };
    }
    return null;
  });

  return (
    <div>
      <div className="mb-4 text-sm font-medium text-foreground">
        {new Date(year, month).toLocaleString("pt-BR", { month: "long", year: "numeric" }).toUpperCase()}
      </div>
      <div className="grid grid-cols-7 gap-px bg-border border border-border rounded-xl overflow-hidden">
        {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
          <div key={d} className="bg-surface py-2 text-center text-xs font-medium text-muted-foreground">{d}</div>
        ))}
        {days.map((d, i) => (
          <div key={i} className={`min-h-[120px] bg-background p-2 ${!d ? "opacity-50 bg-background/50" : ""}`}>
            {d && (
              <>
                <div className={`text-xs font-medium mb-1 ${d.dayNum === today.getDate() ? "text-primary" : "text-muted-foreground"}`}>{d.dayNum}</div>
                <div className="space-y-1.5">
                  {d.events.map((e) => (
                    <Link key={e.id} to="/eventos/$eventoId" params={{ eventoId: e.id }} className="block p-2 rounded-xl border border-primary/20 bg-primary/5 hover:bg-primary/10 transition-all hover:scale-[1.02] cursor-pointer space-y-1">
                      <div className="font-bold text-primary text-[11px] truncate leading-tight">{e.event_name || e.client_name}</div>
                      <div className="text-[9.5px] text-foreground font-semibold truncate">{e.event_type}</div>
                      <div className="text-[9px] text-muted-foreground flex justify-between items-center gap-1">
                        <span>{e.guests} conv.</span>
                        <span className="truncate max-w-[55px] text-right">{e.city || "A definir"}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
